import { useState, useEffect } from 'react'
import { eventbriteService, EventbriteEvent } from '../utils/eventbriteService'
import { exportCalendarToPDF, CalendarExportData } from '../utils/calendarPdfExport'
import { getClosureForDate } from '../utils/calendarClosures'
import SEO from '../components/SEO'

// Use converted Eventbrite class format
type ClassSchedule = ReturnType<typeof eventbriteService.convertToClass>

function EventbriteClasses() {
  const [classes, setClasses] = useState<ClassSchedule[]>([])
  const [eventbriteEvents, setEventbriteEvents] = useState<EventbriteEvent[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'calendar'>('grid')
  const [ticketInfo, setTicketInfo] = useState<Record<string, any[]>>({})
  const [currentCalendarDate, setCurrentCalendarDate] = useState(new Date())
  const [isExportingPDF, setIsExportingPDF] = useState(false)

  // Robust date parsing utility
  const parseClassDate = (dateString: string): Date | null => {
    if (!dateString) return null

    try {
      // Try standard parsing first
      let date = new Date(dateString)
      if (!isNaN(date.getTime())) {
        return date
      }

      // Try with slashes instead of dashes
      date = new Date(dateString.replace(/-/g, '/'))
      if (!isNaN(date.getTime())) {
        return date
      }

      console.warn('Could not parse date:', dateString)
      return null
    } catch (error) {
      console.warn('Date parsing error:', error, 'for date:', dateString)
      return null
    }
  }

  // Load classes from Eventbrite API
  useEffect(() => {
    const loadEventbriteData = async () => {
      console.log('EventbriteClasses: Loading from Eventbrite...')
      setIsLoading(true)
      setConnectionError(null)

      try {
        // Check if Eventbrite is configured
        if (!eventbriteService.isConfigured()) {
          console.log('EventbriteClasses: Eventbrite not configured, showing empty state')
          setClasses([])
          setConnectionError('Eventbrite integration not configured. Please set up API credentials.')
          setIsLoading(false)
          return
        }

        // Fetch events from Eventbrite (error handling in catch block)
        const events = await eventbriteService.getOrganizationEvents(100)
        console.log('EventbriteClasses: Loaded', events.length, 'events from Eventbrite')
        setEventbriteEvents(events)

        // Convert Eventbrite events to class format
        const convertedClasses = events.map(event => eventbriteService.convertToClass(event))
        console.log('EventbriteClasses: Converted to', convertedClasses.length, 'classes')

        // Deduplicate by class ID (defensive check - should already be handled by service)
        const uniqueClasses = Array.from(
          new Map(convertedClasses.map(cls => [cls.id, cls])).values()
        )

        if (uniqueClasses.length < convertedClasses.length) {
          console.warn(`EventbriteClasses: Removed ${convertedClasses.length - uniqueClasses.length} duplicate classes after conversion`)
        }
        console.log('EventbriteClasses: After deduplication:', uniqueClasses.length, 'unique classes')

        // Sort classes by date (earliest first)
        const sortedClasses = uniqueClasses.sort((a, b) => {
          const dateA = parseClassDate(a.date)
          const dateB = parseClassDate(b.date)
          if (!dateA || !dateB) return 0
          return dateA.getTime() - dateB.getTime()
        })

        setClasses(sortedClasses)

        // Fetch ticket information for each event
        const ticketInfoPromises = events.map(async (event) => {
          try {
            const tickets = await eventbriteService.getEventTicketClasses(event.id)
            return { eventId: event.id, tickets }
          } catch (error) {
            console.error(`Failed to fetch tickets for event ${event.id}:`, error)
            return { eventId: event.id, tickets: [] }
          }
        })

        const ticketResults = await Promise.all(ticketInfoPromises)
        const ticketInfoMap: Record<string, any[]> = {}
        ticketResults.forEach(result => {
          ticketInfoMap[result.eventId] = result.tickets
        })
        setTicketInfo(ticketInfoMap)

      } catch (error: any) {
        console.error('EventbriteClasses: Error loading Eventbrite data:', error)
        setConnectionError(`Failed to load classes: ${error.message}`)
        setClasses([])
      } finally {
        setIsLoading(false)
      }
    }

    // Initial load
    loadEventbriteData()

    // Set up refresh interval for live events (every 10 minutes)
    const refreshInterval = setInterval(() => {
      console.log('EventbriteClasses: Refreshing Eventbrite data...')
      loadEventbriteData()
    }, 10 * 60 * 1000) // 10 minutes

    return () => {
      clearInterval(refreshInterval)
    }
  }, [])

  const visibleClasses = Array.isArray(classes) ? classes : []

  const handleBooking = (classItem: ClassSchedule) => {
    // Redirect to Eventbrite for booking
    if (classItem.isEventbriteEvent && classItem.eventbriteData) {
      const eventbriteUrl = eventbriteService.getEventUrl(classItem.eventbriteData.id)
      window.open(eventbriteUrl, '_blank')
    } else {
      // Fallback message
      alert('This class can be booked directly through Eventbrite. Please visit our Eventbrite page.')
    }
  }

  const getEventbriteImage = (classItem: ClassSchedule): string | null => {
    if (classItem.isEventbriteEvent && classItem.eventbriteData?.id) {
      // Find the original Eventbrite event
      const originalEvent = eventbriteEvents.find(event => event.id === classItem.eventbriteData?.id)
      if (originalEvent?.logo?.url) {
        return originalEvent.logo.url
      }
    }
    return null
  }

  const formatClassDate = (dateString: string): string => {
    const date = parseClassDate(dateString)
    if (!date) {
      return `Date: ${dateString}` // Show raw value for debugging
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  // Get pricing information from ticket classes
  const getPricingInfo = (classItem: ClassSchedule) => {
    if (!classItem.eventbriteData?.id) return { minPrice: 0, maxPrice: 0, priceRange: 'Free' }

    const tickets = ticketInfo[classItem.eventbriteData.id] || []
    if (tickets.length === 0) return { minPrice: 0, maxPrice: 0, priceRange: 'TBD' }

    const prices = tickets
      .filter(ticket => ticket.cost && ticket.cost.major_value)
      .map(ticket => parseFloat(ticket.cost.major_value))

    if (prices.length === 0) return { minPrice: 0, maxPrice: 0, priceRange: 'Free' }

    const minPrice = Math.min(...prices)
    const maxPrice = Math.max(...prices)

    if (minPrice === maxPrice) {
      return { minPrice, maxPrice, priceRange: `$${minPrice}` }
    } else {
      return { minPrice, maxPrice, priceRange: `$${minPrice} - $${maxPrice}` }
    }
  }

  // Get availability information from ticket classes
  const getAvailabilityInfo = (classItem: ClassSchedule) => {
    if (!classItem.eventbriteData?.id) return { available: 0, total: 0, status: 'Unknown' }

    // Find the original Eventbrite event to get event-level capacity
    const originalEvent = eventbriteEvents.find(event => event.id === classItem.eventbriteData?.id)
    const eventCapacity = originalEvent?.capacity || 0

    const tickets = ticketInfo[classItem.eventbriteData.id] || []
    if (tickets.length === 0) return { available: 0, total: eventCapacity, status: 'Loading...' }

    // Calculate total tickets sold across all ticket types
    let totalSold = 0
    tickets.forEach(ticket => {
      totalSold += ticket.quantity_sold || 0
    })

    // Calculate available spots from event capacity
    const totalAvailable = eventCapacity - totalSold

    if (totalAvailable <= 0) {
      return { available: 0, total: eventCapacity, status: 'Sold Out' }
    } else if (totalAvailable <= 3) {
      return { available: totalAvailable, total: eventCapacity, status: 'Few Left' }
    } else {
      return { available: totalAvailable, total: eventCapacity, status: 'Available' }
    }
  }

  // Calendar utility functions
  const getCalendarDays = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startDate = new Date(firstDay)
    const endDate = new Date(lastDay)

    // Get start of week (Sunday)
    startDate.setDate(startDate.getDate() - startDate.getDay())

    // Get end of week (Saturday)
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()))

    const days = []
    const current = new Date(startDate)

    while (current <= endDate) {
      days.push(new Date(current))
      current.setDate(current.getDate() + 1)
    }

    return days
  }

  const getClassesForDate = (date: Date) => {
    const getMinutesSinceMidnight = (time: string) => {
      const match = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
      if (!match) return Number.MAX_SAFE_INTEGER

      const [, hour, minute, meridiem] = match
      const hour24 = (Number(hour) % 12) + (meridiem.toUpperCase() === 'PM' ? 12 : 0)
      return hour24 * 60 + Number(minute)
    }

    return visibleClasses.filter(classItem => {
      const classDate = parseClassDate(classItem.date)
      if (!classDate) return false

      return classDate.getDate() === date.getDate() &&
             classDate.getMonth() === date.getMonth() &&
             classDate.getFullYear() === date.getFullYear()
    }).sort((a, b) => {
      return getMinutesSinceMidnight(a.time) - getMinutesSinceMidnight(b.time)
    })
  }

  // Format class for Eventbrite-style display
  const formatEventbriteStyle = (classItem: ClassSchedule) => {
    const classDate = parseClassDate(classItem.date)
    const pricingInfo = getPricingInfo(classItem)

    if (!classDate) {
      return {
        title: classItem.title,
        dateStr: classItem.date,
        dayTime: `${classItem.time}`,
        organization: 'The Salty Mermaid Art',
        price: pricingInfo.priceRange
      }
    }

    // Get day of week and format
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    let dayStr = ''
    if (classDate.toDateString() === today.toDateString()) {
      dayStr = 'Today'
    } else if (classDate.toDateString() === tomorrow.toDateString()) {
      dayStr = 'Tomorrow'
    } else {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      dayStr = dayNames[classDate.getDay()]
    }

    // Format date as "Month Day, Year"
    const dateStr = classDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })

    return {
      title: classItem.title,
      dateStr: dateStr,
      dayTime: `${dayStr} • ${classItem.time}`,
      organization: 'The Salty Mermaid Art',
      price: `From ${pricingInfo.priceRange}`
    }
  }

  const navigateCalendar = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentCalendarDate)
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1)
    } else {
      newDate.setMonth(newDate.getMonth() + 1)
    }
    setCurrentCalendarDate(newDate)
  }

  const handleExportPDF = async () => {
    setIsExportingPDF(true)

    try {
      // Get calendar days for the current month
      const calendarDays = getCalendarDays(currentCalendarDate.getFullYear(), currentCalendarDate.getMonth())

      // Build export data
      const exportData: CalendarExportData = {
        month: currentCalendarDate.toLocaleDateString('en-US', { month: 'long' }),
        year: currentCalendarDate.getFullYear(),
        days: calendarDays.map(day => {
          const dayClasses = getClassesForDate(day)
          const isCurrentMonth = day.getMonth() === currentCalendarDate.getMonth()
          const isWeekend = day.getDay() === 0 || day.getDay() === 6

          return {
            date: day,
            dayNumber: day.getDate(),
            isCurrentMonth,
            isWeekend,
            closureLabel: getClosureForDate(day)?.label,
            classes: dayClasses.map(classItem => {
              const eventbriteFormat = formatEventbriteStyle(classItem)
              const eventbriteImage = getEventbriteImage(classItem)

              return {
                title: classItem.title,
                time: classItem.time,
                price: eventbriteFormat.price,
                imageUrl: eventbriteImage || undefined
              }
            })
          }
        })
      }

      // Export to PDF
      await exportCalendarToPDF(exportData)
    } catch (error) {
      console.error('Error exporting PDF:', error)
    } finally {
      setIsExportingPDF(false)
    }
  }

  // Create Event schemas for upcoming classes (next 10)
  const eventSchemas = eventbriteEvents.slice(0, 10).map(event => ({
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.name?.text || 'Art Class',
    description: event.description?.text || event.summary || 'Join us for an exciting art class at The Salty Mermaid',
    startDate: event.start?.utc || event.start?.local,
    endDate: event.end?.utc || event.end?.local,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: 'The Salty Mermaid',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '1546 Ocean Shore Blvd',
        addressLocality: 'Ormond Beach',
        addressRegion: 'FL',
        postalCode: '32176',
        addressCountry: 'US'
      }
    },
    image: event.logo?.url || 'https://saltymermaidart.com/social-image.jpg',
    organizer: {
      '@type': 'Organization',
      name: 'The Salty Mermaid',
      url: 'https://saltymermaidart.com'
    },
    offers: event.ticket_availability?.minimum_ticket_price ? {
      '@type': 'Offer',
      url: event.url,
      price: (event.ticket_availability.minimum_ticket_price.value / 100).toString(),
      priceCurrency: event.ticket_availability.minimum_ticket_price.currency || 'USD',
      availability: event.ticket_availability.is_sold_out
        ? 'https://schema.org/SoldOut'
        : 'https://schema.org/InStock'
    } : undefined
  }))

  return (
    <div className="py-5">
      <SEO
        title="Art Classes & Workshops - The Salty Mermaid"
        description="Discover your creative potential with art classes and workshops at The Salty Mermaid in Ormond Beach, FL. From beginner-friendly sessions to advanced workshops for every skill level."
        canonicalUrl="https://saltymermaidart.com/classes"
        structuredData={eventSchemas}
      />
      <div className="container">
        <h1 className="display-4 fw-bold text-center mb-4">Art Classes</h1>
        <div className="row justify-content-center mb-5">
          <div className="col-lg-8">
            <div className="card shadow-lg border-0" style={{
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-4 text-center">
                <p className="h5 text-dark mb-0" style={{lineHeight: '1.6'}}>
                  Discover your creative potential with our diverse range of art classes.
                  From beginner-friendly sessions to advanced workshops, we have something for every skill level.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* View Toggle */}
        {!isLoading && !connectionError && classes.length > 0 && (
          <div className="row justify-content-center mb-5 no-print">
            <div className="col-lg-8">
              <section className="classes-view-card" aria-label="Class Schedule and Calendar">
                <div className="classes-view-card__banner">
                  <i className="fas fa-calendar-check" aria-hidden="true"></i>
                  <span>Class Schedule &amp; Calendar</span>
                </div>
                <div className="classes-view-card__body">
                  <p className="classes-view-card__hint">Choose how you want to browse upcoming classes.</p>
                  <div className="classes-view-toggle" role="group" aria-label="Choose a view">
                    <button
                      type="button"
                      className={`classes-view-toggle__btn ${viewMode === 'grid' ? 'is-active' : ''}`}
                      onClick={() => setViewMode('grid')}
                      aria-pressed={viewMode === 'grid'}
                    >
                      <i className="fas fa-th-large" aria-hidden="true"></i>
                      Class Schedule
                    </button>
                    <button
                      type="button"
                      className={`classes-view-toggle__btn ${viewMode === 'calendar' ? 'is-active' : ''}`}
                      onClick={() => setViewMode('calendar')}
                      aria-pressed={viewMode === 'calendar'}
                    >
                      <i className="fas fa-calendar-alt" aria-hidden="true"></i>
                      Calendar
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5>Loading classes from Eventbrite...</h5>
            <p className="text-muted">Please wait while we fetch the latest class information.</p>
          </div>
        )}

        {/* Connection Error State */}
        {connectionError && !isLoading && (
          <div className="col-12 mb-4">
            <div className="alert alert-warning text-center">
              <h5><i className="fas fa-exclamation-triangle me-2"></i>Connection Issue</h5>
              <p className="mb-3">{connectionError}</p>
              <button
                className="btn btn-outline-primary"
                onClick={() => window.location.reload()}
              >
                <i className="fas fa-sync-alt me-2"></i>Retry
              </button>
            </div>
          </div>
        )}

        {/* Classes Display */}
        {!isLoading && !connectionError && (
          <>
            {viewMode === 'calendar' ? (
              /* Calendar View - Microsoft Outlook Style */
              <div className="row justify-content-center">
                <div className="col-12">
                  <div className="card shadow-lg border-0" style={{
                    borderRadius: '20px',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                  }}>
                    <div className="card-body p-4">
                      {/* Calendar Header */}
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h3 className="mb-0 text-primary fw-bold">
                          <i className="fas fa-calendar-alt me-2"></i>
                          {currentCalendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </h3>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-success shadow"
                            onClick={handleExportPDF}
                            disabled={isExportingPDF}
                            title={isExportingPDF ? "Generating PDF..." : "Export Calendar to PDF"}
                            style={{
                              minWidth: '180px',
                              position: 'relative'
                            }}
                          >
                            {isExportingPDF ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Generating PDF...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-file-pdf me-2"></i>
                                Export to PDF
                              </>
                            )}
                          </button>
                          <div className="btn-group shadow">
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => navigateCalendar('prev')}
                              title="Previous Month"
                            >
                              <i className="fas fa-chevron-left me-1"></i>
                              Previous
                            </button>
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => setCurrentCalendarDate(new Date())}
                              title="Go to Today"
                            >
                              Today
                            </button>
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => navigateCalendar('next')}
                              title="Next Month"
                            >
                              Next
                              <i className="fas fa-chevron-right ms-1"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Calendar Grid */}
                      <div className="calendar-grid border rounded overflow-auto shadow-sm" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, minmax(140px, 1fr))',
                        backgroundColor: '#fff',
                        borderRadius: '15px',
                        maxWidth: '100%'
                      }}>
                        {/* Days of Week Header */}
                        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                          <div key={day} className="p-3 text-center fw-bold border-end border-bottom" style={{
                            backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            fontSize: '0.9rem',
                            letterSpacing: '0.5px'
                          }}>
                            <div className="d-none d-md-block">{day}</div>
                            <div className="d-block d-md-none">{day.substring(0, 3)}</div>
                          </div>
                        ))}

                        {/* Calendar Days */}
                        {(() => {
                          const calendarDays = getCalendarDays(currentCalendarDate.getFullYear(), currentCalendarDate.getMonth())

                          return calendarDays.map((day, dayIndex) => {
                            const dayClasses = getClassesForDate(day)
                            const isCurrentMonth = day.getMonth() === currentCalendarDate.getMonth()
                            const isToday = day.toDateString() === new Date().toDateString()
                            const isWeekend = day.getDay() === 0 || day.getDay() === 6
                            const closure = getClosureForDate(day)
                            const dayBackground = closure
                              ? '#fee2e2'
                              : isCurrentMonth
                                ? (isWeekend ? '#fafafa' : '#ffffff')
                                : '#f5f5f5'

                            return (
                              <div
                                key={dayIndex}
                                className="border-end border-bottom p-3 position-relative"
                                style={{
                                  minHeight: '140px',
                                  backgroundColor: dayBackground,
                                  transition: 'background-color 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  if (isCurrentMonth && !closure) {
                                    e.currentTarget.style.backgroundColor = '#f0f9ff'
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = dayBackground
                                }}
                              >
                                {/* Day Number */}
                                <div className={`fw-bold position-relative mb-2 ${
                                  isToday
                                    ? 'text-white bg-primary rounded-circle d-inline-flex align-items-center justify-content-center'
                                    : isCurrentMonth
                                      ? 'text-dark'
                                      : 'text-muted'
                                }`} style={{
                                  fontSize: '0.9rem',
                                  ...(isToday ? {
                                    width: '28px',
                                    height: '28px',
                                    fontSize: '0.85rem'
                                  } : {})
                                }}>
                                  {day.getDate()}
                                </div>

                                {/* Closed banner */}
                                {closure && (
                                  <div className="text-center text-white mb-2" style={{
                                    backgroundColor: '#dc2626',
                                    borderRadius: '8px',
                                    padding: '8px 4px',
                                    boxShadow: '0 2px 4px rgba(220, 38, 38, 0.4)'
                                  }}>
                                    <div className="fw-bold" style={{
                                      fontSize: '1.25rem',
                                      letterSpacing: '1px',
                                      lineHeight: '1.2'
                                    }}>
                                      CLOSED
                                    </div>
                                    <div className="fw-semibold" style={{ fontSize: '0.7rem', lineHeight: '1.2' }}>
                                      {closure.label}
                                    </div>
                                  </div>
                                )}

                                {/* Classes for this day */}
                                <div className="classes-container" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                                  {dayClasses.map((classItem, idx) => {
                                    const eventbriteFormat = formatEventbriteStyle(classItem)
                                    const pricingInfo = getPricingInfo(classItem)
                                    const availabilityInfo = getAvailabilityInfo(classItem)
                                    const eventbriteImage = getEventbriteImage(classItem)

                                    return (
                                      <div
                                        key={classItem.id}
                                        className="card border-0 mb-1 shadow-sm"
                                        style={{
                                          backgroundColor: '#e3f2fd',
                                          fontSize: '0.7rem',
                                          cursor: 'pointer',
                                          transition: 'all 0.2s ease',
                                          borderRadius: '8px'
                                        }}
                                        onClick={() => handleBooking(classItem)}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#bbdefb'
                                          e.currentTarget.style.transform = 'translateY(-1px)'
                                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)'
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = '#e3f2fd'
                                          e.currentTarget.style.transform = 'translateY(0)'
                                          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'
                                        }}
                                      >
                                        <div className="card-body p-2">
                                          <div className="d-flex gap-2 align-items-start">
                                            {/* Thumbnail Image */}
                                            {eventbriteImage && (
                                              <div className="flex-shrink-0" style={{
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '6px',
                                                overflow: 'hidden',
                                                backgroundColor: '#f0f0f0'
                                              }}>
                                                <img
                                                  src={eventbriteImage}
                                                  alt={classItem.title}
                                                  style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                  }}
                                                />
                                              </div>
                                            )}

                                            {/* Class Info */}
                                            <div className="flex-grow-1 overflow-hidden">
                                              <div className="fw-bold mb-1" style={{
                                                color: '#1565c0',
                                                lineHeight: '1.2',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                wordWrap: 'break-word'
                                              }}>
                                                {classItem.title}
                                              </div>
                                              <div className="text-muted" style={{
                                                fontSize: '0.65rem',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                              }}>
                                                {classItem.time}
                                              </div>
                                              <div className="d-flex align-items-center gap-2">
                                                <div className="fw-bold text-success" style={{
                                                  fontSize: '0.65rem',
                                                  whiteSpace: 'nowrap',
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis'
                                                }}>
                                                  {eventbriteFormat.price}
                                                </div>
                                                {/* Green indicator for classes available */}
                                                {availabilityInfo.status === 'Available' && (
                                                  <div
                                                    className="bg-success"
                                                    style={{
                                                      width: '8px',
                                                      height: '8px',
                                                      borderRadius: '2px',
                                                      flexShrink: 0
                                                    }}
                                                    title="Classes Available"
                                                  ></div>
                                                )}
                                                {/* Red indicator for sold out classes */}
                                                {availabilityInfo.status === 'Sold Out' && (
                                                  <div
                                                    className="bg-danger"
                                                    style={{
                                                      width: '8px',
                                                      height: '8px',
                                                      borderRadius: '2px',
                                                      flexShrink: 0
                                                    }}
                                                    title="Class Filled"
                                                  ></div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            )
                          })
                        })()}
                      </div>

                      {/* Legend and Instructions */}
                      <div className="mt-4">
                        <div className="row">
                          <div className="col-md-8">
                            <div className="d-flex align-items-center text-muted">
                              <i className="fas fa-info-circle me-2 text-primary"></i>
                              <small>Click on any class to book through Eventbrite. Navigate months using the arrow buttons above.</small>
                            </div>
                          </div>
                          <div className="col-md-4 text-end">
                            <div className="d-flex align-items-center justify-content-end flex-wrap gap-2">
                              <div className="d-flex align-items-center">
                                <div className="bg-primary rounded-circle me-2" style={{ width: '12px', height: '12px' }}></div>
                                <small className="text-muted">Today</small>
                              </div>
                              <div className="d-flex align-items-center">
                                <div className="bg-success me-2" style={{ width: '12px', height: '12px', borderRadius: '3px' }}></div>
                                <small className="text-muted">Classes Available</small>
                              </div>
                              <div className="d-flex align-items-center">
                                <div className="bg-danger me-2" style={{ width: '12px', height: '12px', borderRadius: '3px' }}></div>
                                <small className="text-muted">Class Filled</small>
                              </div>
                              <div className="d-flex align-items-center">
                                <div className="me-2" style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: '#dc2626' }}></div>
                                <small className="text-muted">Closed</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Grid View */
              <div className="row g-4">
                {visibleClasses.length > 0 ? (
                  visibleClasses.map((classItem) => {
                const eventbriteImage = getEventbriteImage(classItem)
                const eventbriteFormat = formatEventbriteStyle(classItem)
                const availabilityInfo = getAvailabilityInfo(classItem)

                return (
                  <div key={classItem.id} className="col-xl-4 col-lg-6">
                    <div className="card h-100 border-0 shadow-lg overflow-hidden position-relative"
                         style={{
                           borderRadius: '20px',
                           transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                           background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                         }}
                         onMouseEnter={(e) => {
                           e.currentTarget.style.transform = 'translateY(-8px)'
                           e.currentTarget.style.boxShadow = '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                         }}
                         onMouseLeave={(e) => {
                           e.currentTarget.style.transform = 'translateY(0px)'
                           e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                         }}>

                      {/* Hero Image Section */}
                      <div className="position-relative" style={{height: '280px', overflow: 'hidden'}}>
                        {eventbriteImage ? (
                          <>
                            <img
                              src={eventbriteImage}
                              alt={classItem.title}
                              className="w-100 h-100"
                              style={{
                                objectFit: 'cover',
                                objectPosition: 'center',
                                transition: 'transform 0.6s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.05)'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)'
                              }}
                            />
                            <div className="position-absolute top-0 start-0 w-100 h-100"
                                 style={{
                                   background: 'linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%)'
                                 }}>
                            </div>
                          </>
                        ) : (
                          <div className="w-100 h-100 d-flex align-items-center justify-content-center"
                               style={{
                                 background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                 color: 'white'
                               }}>
                            <i className="fas fa-palette" style={{fontSize: '4rem', opacity: 0.8}}></i>
                          </div>
                        )}

                        {/* Difficulty Badge */}
                        <div className="position-absolute top-3 end-3">
                          <span className="badge px-3 py-2 fw-bold text-uppercase letter-spacing-wide"
                                style={{
                                  backgroundColor: classItem.difficulty === 'Beginner' ? '#10b981' :
                                                  classItem.difficulty === 'Intermediate' ? '#f59e0b' :
                                                  classItem.difficulty === 'Advanced' ? '#ef4444' : '#3b82f6',
                                  color: 'white',
                                  borderRadius: '50px',
                                  fontSize: '0.75rem',
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                }}>
                            {classItem.difficulty}
                          </span>
                        </div>

                        {/* Eventbrite Badge */}
                        <div className="position-absolute top-3 start-3">
                          <div className="badge px-3 py-2 fw-bold"
                               style={{
                                 backgroundColor: 'rgba(255, 111, 97, 0.9)',
                                 color: 'white',
                                 backdropFilter: 'blur(10px)',
                                 borderRadius: '50px',
                                 fontSize: '0.75rem'
                               }}>
                            <i className="fas fa-external-link-alt me-1"></i>
                            Live on Eventbrite
                          </div>
                        </div>
                      </div>

                      {/* Content Section - Eventbrite Format */}
                      <div className="p-4">
                        {/* Date Badge */}
                        {eventbriteFormat.dateStr && (
                          <div className="mb-2">
                            <span className="badge bg-primary" style={{ fontSize: '0.85rem' }}>
                              {eventbriteFormat.dateStr}
                            </span>
                          </div>
                        )}

                        {/* Eventbrite-style Title */}
                        <h3 className="h5 fw-bold mb-2"
                            style={{
                              color: '#1f2937',
                              lineHeight: '1.3',
                              fontWeight: '700'
                            }}>
                          {eventbriteFormat.title}
                        </h3>

                        {/* Day and Time */}
                        <p className="mb-3 fw-semibold"
                           style={{
                             color: '#374151',
                             fontSize: '0.95rem'
                           }}>
                          {eventbriteFormat.dayTime}
                        </p>

                        {/* Organization */}
                        <p className="mb-3 fw-semibold"
                           style={{
                             color: '#6b7280',
                             fontSize: '0.9rem'
                           }}>
                          {eventbriteFormat.organization}
                        </p>

                        {/* Price */}
                        <div className="mb-4">
                          <div className="h5 fw-bold mb-0" style={{color: '#059669'}}>
                            {eventbriteFormat.price}
                          </div>
                          <div className="small" style={{color: '#9ca3af'}}>
                            {availabilityInfo.status === 'Sold Out' ?
                              <span className="text-danger fw-bold">Sold Out</span> :
                              availabilityInfo.status === 'Few Left' ?
                              <span className="text-warning fw-bold">Only {availabilityInfo.available} left!</span> :
                              `${availabilityInfo.available} spots available`
                            }
                          </div>
                        </div>

                        {/* Book Button */}
                        <button
                          className="btn px-4 py-2 fw-bold rounded-pill btn-primary w-100"
                          onClick={() => handleBooking(classItem)}
                          style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            boxShadow: '0 4px 6px -1px rgba(102, 126, 234, 0.3)',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)'
                            e.currentTarget.style.boxShadow = '0 8px 12px -1px rgba(102, 126, 234, 0.4)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0px)'
                            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(102, 126, 234, 0.3)'
                          }}>
                          <i className="fas fa-external-link-alt me-2"></i>
                          Book on Eventbrite
                        </button>
                      </div>
                    </div>
                  </div>
                )
                  })
                ) : (
                  <div className="col-12">
                    <div className="alert alert-info text-center">
                      <h5>No classes match your current filters</h5>
                      <p className="mb-0">Try adjusting your filter selections to see more classes.</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {classes.length === 0 && !isLoading && !connectionError && (
          <div className="text-center py-5">
            <div className="display-1 mb-4">🎨</div>
            <h3 className="text-white">No Classes Available</h3>
            <p className="text-white">No classes are currently scheduled. Check back soon for new events!</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default EventbriteClasses